import { useEffect, useRef, useState } from 'react';
import { getEditableForm } from '../../api/forms-api';
import { getMySubmissionDetail, getMySubmissions } from '../../api/my-submissions-api';
import { useUiLocale } from '../../app/ui-locale';
import { ErrorMessage, Loading } from '../../components/Loading';
import { Pagination } from '../../components/Pagination';
import type { DynamicFormDefinition, FormValues } from '../../models/form';
import type { MySubmissionDetail, MySubmissionListItem } from '../../models/my-submissions';
import { printDynamicFormsElement } from '../../utils/print-document';
import { FormRenderer } from '../renderer/FormRenderer';
import { publicCopy, type PublicCopy } from '../renderer/public-copy';

interface Props {
  pageSize?: number;
  showStatus?: boolean;
  showPrint?: boolean;
}

function statusText(item: MySubmissionListItem, copy: PublicCopy): string {
  return item.StatusTitle || item.StatusText || copy.registeredStatus;
}

export function MySubmissionsApp({ pageSize = 10, showStatus = true, showPrint = true }: Props) {
  const { locale, formatDateTime } = useUiLocale();
  const copy = publicCopy(locale);
  const [items, setItems] = useState<MySubmissionListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [selected, setSelected] = useState<(MySubmissionDetail & { ParsedFormData: FormValues })>();
  const [definition, setDefinition] = useState<DynamicFormDefinition>();
  const [detailLoading, setDetailLoading] = useState(false);
  const printRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    getMySubmissions({ PageIndex: pageIndex, PageSize: pageSize, Search: appliedSearch }, controller.signal)
      .then((result) => { setItems(result.Items ?? []); setTotalCount(result.TotalCount ?? 0); })
      .catch((loadError) => { if (!controller.signal.aborted) setError(loadError); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [appliedSearch, pageIndex, pageSize]);


  const openDetail = async (item: MySubmissionListItem) => {
    setDetailLoading(true);
    setError(undefined);
    try {
      const [detail, form] = await Promise.all([getMySubmissionDetail(item.SubmittedFormId), getEditableForm(item.FormId)]);
      setSelected(detail);
      setDefinition(form);
      window.requestAnimationFrame(() => document.querySelector('.df-my-submission-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    } catch (detailError) { setError(detailError); }
    finally { setDetailLoading(false); }
  };

  if (selected && definition) {
    return <section ref={printRef} className="df-my-submissions df-my-submission-detail df-print-source">
      <header className="df-page-header no-print">
        <div><h2>{selected.FormTitle}</h2><p>{copy.trackingCode} <strong>{selected.SubmitCode}</strong> — {formatDateTime(selected.FilledOn)}</p></div>
        <div className="btn-group"><button type="button" className="btn btn-default" onClick={() => { setSelected(undefined); setDefinition(undefined); }}>{copy.backToList}</button>{showPrint ? <button type="button" className="btn btn-primary" onClick={() => printDynamicFormsElement(printRef.current, { title: selected.FormTitle })}>{copy.print}</button> : null}</div>
      </header>
      <div className="df-print-only-header print-only"><h1>{selected.FormTitle}</h1><p>{copy.trackingCode} {selected.SubmitCode} — {formatDateTime(selected.FilledOn)}</p></div>
      {showStatus && (selected.StatusTitle || selected.StatusText) ? <div className="alert alert-info df-my-submission-status"><strong>{selected.StatusTitle || copy.status}</strong>{selected.StatusText ? <span> — {selected.StatusText}</span> : null}</div> : null}
      <FormRenderer form={definition} initialValues={selected.ParsedFormData} readOnly showAllReadOnlyTabs omitVerificationFields secureSubmissionId={selected.SubmittedFormId} />
    </section>;
  }

  return <section className="df-my-submissions">
    <header className="df-page-header"><div><h2>{copy.mySubmissionsTitle}</h2><p>{copy.mySubmissionsDescription}</p></div></header>
    <form className="df-my-submissions-toolbar no-print" onSubmit={(event) => { event.preventDefault(); setPageIndex(0); setAppliedSearch(search.trim()); }}>
      <label className="sr-only" htmlFor="df-my-submissions-search">{copy.search}</label>
      <input id="df-my-submissions-search" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={copy.searchPlaceholder} maxLength={100} />
      <button className="btn btn-primary" type="submit">{copy.search}</button>
      {appliedSearch ? <button className="btn btn-default" type="button" onClick={() => { setSearch(''); setAppliedSearch(''); setPageIndex(0); }}>{copy.clearSearch}</button> : null}
    </form>
    {error ? <ErrorMessage error={error} /> : null}
    {loading ? <Loading /> : items.length === 0 ? <div className="alert alert-info df-my-submissions-empty">{copy.noMySubmissions}</div> : <>
      <div className="table-responsive"><table className="table table-striped table-hover df-my-submissions-table"><thead><tr><th>{copy.formTitle}</th><th>{copy.submittedOn}</th><th>{copy.trackingCode}</th>{showStatus ? <th>{copy.status}</th> : null}<th className="no-print">{copy.operation}</th></tr></thead><tbody>{items.map((item) => <tr key={item.SubmittedFormId}><td data-title={copy.formTitle}>{item.FormTitle}</td><td data-title={copy.submittedOn}>{formatDateTime(item.FilledOn)}</td><td data-title={copy.trackingCode}><code>{item.SubmitCode}</code></td>{showStatus ? <td data-title={copy.status}><span className="label label-info">{statusText(item, copy)}</span></td> : null}<td data-title={copy.operation} className="no-print"><button type="button" className="btn btn-primary btn-xs" disabled={detailLoading} onClick={() => void openDetail(item)}>{copy.view}</button></td></tr>)}</tbody></table></div>
      <Pagination page={pageIndex + 1} pageSize={pageSize} total={totalCount} onChange={(page) => setPageIndex(Math.max(0, page - 1))} />
    </>}
  </section>;
}
